import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Lock, CreditCard, BookOpen, Zap } from 'lucide-react';
import { CTAButton, CHECKOUT_URL } from './Shared';
const heroImage = {
  src: 'https://cdn.magicpatterns.com/uploads/9NGJmUWXJCDSEa54t5Tp3p/Portada_Mokup_NUEVO_CHIKUNG.png',
  alt: 'Mega Pack Chi Kung: los 3 tomos, las plantillas clínicas y los 6 bonos de regalo'
};
const trustRowOne = [{
  icon: ShieldCheck,
  label: 'Garantía 7 días'
}, {
  icon: Lock,
  label: 'Pago seguro'
}, {
  icon: CreditCard,
  label: 'Tarjeta o PayPal'
}];
const trustRowTwo = [{
  icon: BookOpen,
  label: '100% PDF'
}, {
  icon: Zap,
  label: 'Acceso en 5 min'
}];
export const Hero = () => {
  return <header className="relative overflow-hidden" data-id="element-96" data-mp="ncq4lneem4s1">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden" data-id="element-97" data-mp="neawf59c39m7">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[760px] h-[760px] max-w-[130vw] rounded-full bg-brand-goldLight/25 blur-[150px]" data-id="element-98" data-mp="382fdzkwc8ydc" />
        <div className="absolute top-16 right-[3%] font-display text-[96px] md:text-[170px] leading-none text-brand-purpleDark/[0.06] select-none animate-float-soft" style={{
        animationDelay: '0.8s'
      }} data-id="element-99" data-mp="3j9ahvf8c4u67">
          氣
        </div>
        <div className="absolute bottom-16 left-[3%] font-display text-[80px] md:text-[140px] leading-none text-brand-purpleDark/[0.05] select-none animate-float-soft" style={{
        animationDelay: '2s'
      }} data-id="element-100" data-mp="3e8mtl1bhqrfo">
          和
        </div>
        <div className="hidden lg:block absolute top-[18%] left-[4%] w-56 h-56 rounded-full border border-brand-gold/25" data-id="element-101" data-mp="35au9zkyqkxmh" />
        <div className="hidden lg:block absolute bottom-[12%] right-[4%] w-48 h-48 rounded-full border border-brand-purpleDark/10" data-id="element-102" data-mp="2violwmblv0ck" />
      </div>

      {/* Above the fold: headline, mockup and CTA — no scroll needed on mobile */}
      <motion.div initial={{
      opacity: 0,
      y: 14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.7
    }} className="relative z-10 mx-auto flex min-h-[calc(100svh-44px)] w-full max-w-6xl flex-col items-center justify-center gap-[0.7vh] px-3 pt-1 pb-1.5 text-center sm:px-6 md:min-h-0 md:gap-5 md:py-14" data-id="element-103" data-mp="2ljpaivzggqlv">
        <p className="shrink-0 font-seal text-[11px] md:text-sm uppercase tracking-seal text-brand-goldDark underline decoration-brand-gold/70 decoration-1 underline-offset-4" data-id="element-104" data-mp="i9vqp37erylb">
          Lumen Akasha
        </p>

        {/* Exactly two lines, at every width */}
        <h1 className="shrink-0 font-display font-bold text-brand-burgundy" style={{
        fontSize: 'clamp(1.95rem, 8vw, 4.5rem)',
        lineHeight: 1.02,
        letterSpacing: '-0.02em'
      }} data-id="element-105" data-mp="163454qskhsby">
          <span className="block whitespace-nowrap" data-id="element-106" data-mp="3lsypyre788fd">
            Sesiones más{' '}
            <span className="relative inline-block" data-id="element-107" data-mp="3lqm2jypo95ux">
              <span aria-hidden="true" className="absolute inset-x-[-4px] bottom-[0.08em] h-[0.26em] bg-brand-goldLight/60" data-id="element-108" data-mp="3hqalsx0fjpoq" />
              <span className="relative" data-id="element-109" data-mp="21aeirgr7ylam">efectivas</span>
            </span>
          </span>
          <span className="block whitespace-nowrap" data-id="element-110" data-mp="26a9afmitjewq">y sin improvisar.</span>
        </h1>

        <p className="mx-auto max-w-[32ch] shrink-0 font-display italic text-brand-textLight/85" style={{
        fontSize: "20px",
        lineHeight: 1.18,
        paddingBottom: "41px"
      }} data-id="element-111" data-mp="2b0plx132wqgg">
          El conocimiento ya lo tienes. El sistema para aplicarlo, ahora
          también.
        </p>

        {/* Mockup with a luminous gold halo */}
        <div className="relative w-full min-h-0 flex-1 max-h-[42svh] flex items-center justify-center sm:max-w-xl sm:max-h-[46svh] md:max-w-[44rem] md:flex-none md:h-[40vh] md:max-h-none" data-id="element-112" data-mp="2ozz5maqhu2p6">
          <span aria-hidden="true" className="pointer-events-none absolute inset-[12%] -z-10 rounded-full bg-brand-goldLight/20 blur-2xl" data-id="element-113" data-mp="umpqa2b9nmz0" />
          <img src={heroImage.src} alt={heroImage.alt} className="h-full w-full object-contain object-top gold-glow" draggable={false} decoding="async" data-id="element-114" data-mp="zndqneldxg5w" />
        </div>

        <div className="flex w-full shrink-0 flex-col items-center gap-2.5 md:gap-3.5" data-id="element-115" data-mp="e9tqnqklxx1t">
          <CTAButton variant="purple" href={CHECKOUT_URL} className="w-auto max-w-full rounded-sm cta-shimmer relative overflow-hidden font-seal px-5 py-2.5 text-[12px] md:text-[13px] tracking-[0.08em] font-semibold" data-id="element-116" data-mp="99xlvp4361l7">
            <span className="relative z-10" data-id="element-117" data-mp="3cp33yx7uzty0">Quiero mi Mega Pack</span>
          </CTAButton>

          <div className="flex flex-col items-center gap-1.5 text-[11px] md:text-xs text-brand-muted" data-id="element-118" data-mp="3ri6jz05sxzmm">
            <ul className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5" data-id="element-119" data-mp="3mnag92r7dnto">
              {trustRowOne.map(({
              icon: Icon,
              label
            }) => <li key={label} className="inline-flex items-center gap-1.5" data-id="element-120" data-mp="2tzmz5xp55fbs">
                  <Icon className="w-3.5 h-3.5 text-brand-muted/80 shrink-0" data-id="element-121" data-mp="19i5eyjwd5aho" />
                  {label}
                </li>)}
            </ul>
            <ul className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5" data-id="element-122" data-mp="1eaov9omfqjq6">
              {trustRowTwo.map(({
              icon: Icon,
              label
            }) => <li key={label} className="inline-flex items-center gap-1.5" data-id="element-123" data-mp="14isu24p1ydhq">
                  <Icon className="w-3.5 h-3.5 text-brand-muted/80 shrink-0" data-id="element-124" data-mp="1s9eq0xls87bd" />
                  {label}
                </li>)}
            </ul>
          </div>
        </div>

        <p className="mx-auto max-w-[34ch] shrink-0 font-display text-brand-purpleDark text-[14px] leading-snug sm:text-base md:text-lg" data-id="element-125" data-mp="1x5vmcf34xrzj">
          Pasa de la planificación caótica a una práctica clínica profesional
          hoy mismo. 🧘‍♂️
        </p>
      </motion.div>
    </header>;
};